import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BackgroundProvider } from "./context/BackgroundContext";
import BackgroundsSettings from "./pages/settings/Backgrounds";
import QuotePdfContent from "./pages/config/QuotePdfContent";
import PipelineAutomation from "./pages/config/PipelineAutomation";
import AccountManagerSync from "./pages/config/AccountManagerSync";
import TradeAccountDiscounts from "./pages/config/TradeAccountDiscounts";
import Integrations from "./pages/config/Integrations";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import Items from "./pages/Items";
import NewItem from "./pages/NewItem";
import ItemDetail from "./pages/ItemDetail";
import AdminUsers from "./pages/AdminUsers";
import WebhookLog from "./pages/admin/WebhookLog";
import ProductManagement from "./pages/admin/ProductManagement";
import OrderManagement from "./pages/admin/OrderManagement";
// CustomerManagement retired (customers collection dropped)
import PersonManagement from "./pages/admin/PersonManagement";
import CallManagement from "./pages/admin/CallManagement";
import QuoteManagement from "./pages/admin/QuoteManagement";
import SupplierManagement from "./pages/admin/SupplierManagement";
import LabProducts from "./pages/lab/LabProducts";
import LabHealth from "./pages/lab/LabHealth";
import LabEnrichmentCoverage from "./pages/lab/LabEnrichmentCoverage";
import LabProductDetail from "./pages/lab/LabProductDetail";
import LabOrders from "./pages/lab/LabOrders";
import LabOrderDetail from "./pages/lab/LabOrderDetail";
import LabWorkflows from "./pages/lab/LabWorkflows";
// LabCustomerDetail retired (customers collection dropped)
import LabPersons from "./pages/lab/LabPersons";
import CRMPersonsList from "./pages/crm/CRMPersonsList";
import SalesVelocity from "./pages/crm/velocity/SalesVelocity";
import CRMPerson360 from "./pages/crm/CRMPerson360";
import MediaLibrary from "./pages/media/MediaLibrary";
import WorkspaceLayout from "./pages/workspace/WorkspaceLayout";
import WorkspaceAdmin from "./pages/workspace/Admin";
import WorkspaceOrgChart from "./pages/workspace/OrgChart";
import WorkspaceFullSquad from "./pages/workspace/FullSquad";
import WorkspaceAudit from "./pages/workspace/AdminAuditLog";
import WorkspaceTasks from "./pages/workspace/Tasks";
import WorkspaceMyCRM from "./pages/workspace/crm/MyCRM";
import NotificationsPage from "./pages/NotificationsPage";
import { ManagementTasks, ManagementSalesSla } from "./pages/management/TaskOverwatch";
import QuoteLanding from "./pages/quotes/QuoteLanding";
import OrdersDashboard from "./pages/orders/OrdersDashboard";
import ParkedOrders from "./pages/orders/ParkedOrders";
import DealPipeline from "./pages/deals/DealPipeline";
import OrphanQuotes from "./pages/deals/OrphanQuotes";
import LeadPipeline from "./pages/leads/LeadPipeline";
import AgentLibrary from "./pages/lara/AgentLibrary";
import PipelineWorkflows from "./pages/lara/PipelineWorkflows";
import LaraKnowledgeBase from "./pages/lara/KnowledgeBase";
import LaraAIConfig from "./pages/lara/AIConfig";
import LabPersonDetail from "./pages/lab/LabPersonDetail";
import LabCalls from "./pages/lab/LabCalls";
import LabCallDetail from "./pages/lab/LabCallDetail";
import LabSuppliers from "./pages/lab/LabSuppliers";
import LabSupplierDetail from "./pages/lab/LabSupplierDetail";
import ViewQuotes from "./pages/quotes/ViewQuotes";
import MyQuotes from "./pages/quotes/MyQuotes";
import AllQuotes from "./pages/quotes/AllQuotes";
import NewQuote from "./pages/quotes/NewQuote";
import QuoteBuilder from "./pages/quotes/QuoteBuilder";
import MyDashboard from "./pages/dashboard/MyDashboard";
import AMManagement from "./pages/admin/AMManagement";
import MyCustomers from "./pages/dashboard/MyCustomers";
import ManagementActivities from "./pages/management/Activities";
import AMReports from "./pages/management/AMReports";
import SquadReports from "./pages/management/SquadReports";
import StaffProductivity from "./pages/management/StaffProductivity";
import QuotePdfPreview from "./pages/quotes/QuotePdfPreview";
import Hub from "./pages/hub/Hub";
import Calls from "./pages/calls/Calls";
import TrainingAdmin from "./pages/config/TrainingAdmin";
import WhatsApp from "./pages/comms/WhatsApp";
import Profile from "./pages/profile/Profile";
import Shell from "./components/Shell";
import { Toaster } from "sonner";
import "./App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-400">Loading...</div>;
  if (user) return <Navigate to="/home" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/home" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <BackgroundProvider>
          <ErrorBoundary
            fallback={(err) => (
              <div className="min-h-screen grid place-items-center bg-[#0B1120] text-slate-200 p-6" data-testid="app-error-boundary">
                <div className="text-center max-w-lg">
                  <div className="text-lg font-semibold mb-2">Something went wrong on this page</div>
                  <div className="text-[11px] text-slate-500 font-mono break-all mb-5">{String(err?.message || err)}</div>
                  <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium hover:bg-orange-600">
                    Reload page
                  </button>
                </div>
              </div>
            )}
          >
          <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
          <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />

          {/* Everything below shares the persistent app Shell (sidebar + header + ⌘K).
              Shell mounts ONCE at the router level and renders <Outlet /> — the
              page swaps on navigation but the shell (and its state, scroll,
              dropdowns, keyboard shortcuts) stays alive. */}
          <Route element={<Shell />}>
          <Route path="/quote/:ref" element={<QuoteLanding />} />
          <Route path="/home" element={<Protected><Home /></Protected>} />
          <Route path="/my-dashboard" element={<Protected><MyDashboard /></Protected>} />
          <Route path="/my-dashboard/customers" element={<Protected><MyCustomers /></Protected>} />
          <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />
          <Route path="/management/tasks" element={<Protected><ManagementTasks /></Protected>} />
          <Route path="/management/productivity" element={<Protected><StaffProductivity /></Protected>} />
          <Route path="/management/sales-sla" element={<Protected><ManagementSalesSla /></Protected>} />
          <Route path="/management/activities" element={<Protected><ManagementActivities /></Protected>} />
          <Route path="/management/account-managers" element={<Protected><AMReports /></Protected>} />
          <Route path="/management/squad" element={<Protected><SquadReports /></Protected>} />
          <Route path="/management/am-management" element={<Protected><AMManagement /></Protected>} />
          <Route path="/admin/am-management" element={<Navigate to="/management/am-management" replace />} />
          <Route path="/items/mine" element={<Protected><Navigate to="/items/all" replace /></Protected>} />
          <Route path="/items/all" element={<Protected><Items /></Protected>} />
          <Route path="/bugfeatures" element={<Navigate to="/items/all" replace />} />
          <Route path="/items/new" element={<Protected><Navigate to="/items/all" replace /></Protected>} />
          <Route path="/items/:id" element={<Protected><ItemDetail /></Protected>} />
          <Route path="/quotes" element={<Protected><ViewQuotes /></Protected>} />
          <Route path="/quotes/mine" element={<Protected><MyQuotes /></Protected>} />
          <Route path="/quotes/all" element={<Protected><AllQuotes /></Protected>} />
          <Route path="/quotes/new" element={<Protected><QuoteBuilder mode="new" /></Protected>} />
          <Route path="/quotes/overview" element={<Navigate to="/deals" replace />} />
          <Route path="/deals" element={<Protected><DealPipeline /></Protected>} />
          <Route path="/deals/orphan-quotes" element={<Protected><OrphanQuotes /></Protected>} />
          <Route path="/leads" element={<Protected><LeadPipeline /></Protected>} />
          <Route path="/crm/leads" element={<Navigate to="/leads" replace />} />
          <Route path="/quotes/legacy-new" element={<Protected><NewQuote /></Protected>} />
          <Route path="/quotes/:id/edit" element={<Protected><QuoteBuilder mode="edit" /></Protected>} />
          <Route path="/quotes/:id/pdf" element={<Protected><QuotePdfPreview /></Protected>} />
          <Route path="/hub" element={<Protected><Hub /></Protected>} />
          <Route path="/media" element={<Protected><MediaLibrary /></Protected>} />
          <Route path="/calls" element={<Protected><Calls /></Protected>} />
          <Route path="/comms/whatsapp" element={<Protected><WhatsApp /></Protected>} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/admin/users" element={<AdminOnly><AdminUsers /></AdminOnly>} />
          <Route path="/admin/webhook-log" element={<AdminOnly><WebhookLog /></AdminOnly>} />
          <Route path="/admin/products" element={<AdminOnly><ProductManagement /></AdminOnly>} />
          <Route path="/admin/orders" element={<AdminOnly><OrderManagement /></AdminOnly>} />
          {/* Customers page + collection retired — persons is canonical */}
          <Route path="/admin/persons" element={<AdminOnly><PersonManagement /></AdminOnly>} />
          <Route path="/admin/calls" element={<AdminOnly><CallManagement /></AdminOnly>} />
          <Route path="/admin/suppliers" element={<AdminOnly><SupplierManagement /></AdminOnly>} />
          <Route path="/admin/quotes" element={<AdminOnly><QuoteManagement /></AdminOnly>} />
          <Route path="/lab" element={<Protected><LabHealth /></Protected>} />
          <Route path="/lab/health" element={<Protected><LabHealth /></Protected>} />
          <Route path="/lab/enrichment" element={<Protected><LabEnrichmentCoverage /></Protected>} />
          <Route path="/lab/products" element={<Protected><LabProducts /></Protected>} />
          <Route path="/orders/dashboard" element={<Protected><OrdersDashboard /></Protected>} />
          <Route path="/orders/parked" element={<Protected><ParkedOrders /></Protected>} />
          <Route path="/lab/products/:id" element={<Protected><LabProductDetail /></Protected>} />
          <Route path="/lab/workflows" element={<Protected><LabWorkflows /></Protected>} />
          <Route path="/lab/orders" element={<Protected><LabOrders /></Protected>} />
          <Route path="/lab/orders/:id" element={<Protected><LabOrderDetail /></Protected>} />
          {/* Customers page superseded by Persons — legacy paths redirect */}
          {/* Customers Lab views retired — collection dropped (no redirect; falls through to catch-all) */}
          <Route path="/lab/persons" element={<Protected><LabPersons /></Protected>} />
          <Route path="/crm/customers"    element={<Protected><CRMPersonsList /></Protected>} />
          <Route path="/crm/velocity"     element={<Protected><SalesVelocity /></Protected>} />
          <Route path="/crm/persons/:id"  element={<Protected><CRMPerson360 /></Protected>} />
          <Route path="/crm/pipeline"     element={<Navigate to="/deals" replace />} />
          <Route path="/lara/library"     element={<Protected><AgentLibrary /></Protected>} />
          <Route path="/lara/knowledge"   element={<Protected><LaraKnowledgeBase /></Protected>} />
          <Route path="/lara/ai-config"   element={<Protected><LaraAIConfig /></Protected>} />
          <Route path="/lara/pipeline-workflows" element={<Protected><PipelineWorkflows /></Protected>} />

          {/* Workspace — task + activity hub (Wave N) */}
          <Route path="/workspace" element={<Protected><WorkspaceLayout /></Protected>}>
            <Route index element={<Navigate to="my-tasks" replace />} />
            <Route path="home"      element={<Navigate to="/workspace/my-tasks" replace />} />
            <Route path="crm"       element={<WorkspaceMyCRM />} />
            <Route path="tasks"     element={<WorkspaceTasks />} />
            <Route path="my-tasks"  element={<WorkspaceTasks />} />
            <Route path="teams"     element={<Navigate to="/workspace/my-tasks?scope=teams" replace />} />
            <Route path="overwatch" element={<Navigate to="/management/tasks" replace />} />
            <Route path="org-chart" element={<WorkspaceOrgChart />} />
            <Route path="full-squad" element={<WorkspaceFullSquad />} />
            <Route path="audit"     element={<WorkspaceAudit />} />
            <Route path="admin"     element={<WorkspaceAdmin />} />
          </Route>
          <Route path="/lab/persons/:id" element={<Protected><LabPersonDetail /></Protected>} />
          <Route path="/lab/calls" element={<Protected><LabCalls /></Protected>} />
          <Route path="/lab/calls/:id" element={<Protected><LabCallDetail /></Protected>} />
          <Route path="/lab/suppliers" element={<Protected><LabSuppliers /></Protected>} />
          <Route path="/lab/suppliers/:id" element={<Protected><LabSupplierDetail /></Protected>} />
          <Route path="/settings/backgrounds" element={<Protected><BackgroundsSettings /></Protected>} />
          <Route path="/config/quote-pdf" element={<AdminOnly><QuotePdfContent /></AdminOnly>} />
          <Route path="/config/pipeline-automation" element={<AdminOnly><PipelineAutomation /></AdminOnly>} />
          <Route path="/config/account-manager-sync" element={<AdminOnly><AccountManagerSync /></AdminOnly>} />
          <Route path="/config/trade-discounts" element={<AdminOnly><TradeAccountDiscounts /></AdminOnly>} />
          <Route path="/config/integrations" element={<AdminOnly><Integrations /></AdminOnly>} />
          <Route path="/config/training" element={<AdminOnly><TrainingAdmin /></AdminOnly>} />
          </Route>

          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
        </ErrorBoundary>
        </BackgroundProvider>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" closeButton visibleToasts={4} />
    </AuthProvider>
  );
}
